import { Box, RadioGroup, Typography } from '@mui/material';
import { Theme } from '@mui/system';
import { ProductListDiaryHistory, addDays, subtractMonths, truncateTime } from 'common';
import { OrderHistoryDateCardSelection } from 'common/src/models/order.enums';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { CfUncontrolledDrawer } from '../../../../cf-ui/Drawer/CfUncontrolledDrawer';
import { CfDrawerHeader } from '../../../../cf-ui/Drawer/components/CfDrawerHeader';
import { CfUncontrolledMenu } from '../../../../cf-ui/Menu/CfUncontrolledMenu';
import { CfRadio, RadioItem } from '../../../../cf-ui/RadioList/Radio/CfRadio';
import { CfMultipleDatePicker } from '../../../../cf-ui/date-pickers/DateRangePicker/CfMultipleDatePicker';

interface ODateMenuProps {
  isOpen: boolean;
  startDate: Date;
  endDate: Date;
  currentOption: OrderHistoryDateCardSelection;
  onClose: () => void;
  onChange: (startDate: Date, endDate: Date, selectionOption: OrderHistoryDateCardSelection) => void;
  onInit?: (startDate: Date, endDate: Date, selectionOption: OrderHistoryDateCardSelection) => void;
  anchorRef: HTMLElement | null | undefined;
  showDrawer: boolean;
}

interface DateMapModel {
  label: string;
  value: string;
}

export const DateMap: DateMapModel[] = [
  {
    label: 'Last 7 days',
    value: OrderHistoryDateCardSelection.Last7Days,
  },
  {
    label: 'Last 30 days',
    value: OrderHistoryDateCardSelection.Last30Days,
  },
  {
    label: 'Last 90 days',
    value: OrderHistoryDateCardSelection.Last90Days,
  },
  {
    label: 'Last 12 months',
    value: OrderHistoryDateCardSelection.Last12Months,
  },
  {
    label: 'Year to date',
    value: OrderHistoryDateCardSelection.YearToDate,
  },
  {
    label: 'Custom date',
    value: OrderHistoryDateCardSelection.Custom,
  },
];

const ProductListDiaryDateMenu = (props: ODateMenuProps): JSX.Element => {
  const history = useHistory<ProductListDiaryHistory>();
  const radioRefs = DateMap.map(() => useRef<HTMLInputElement>(null));
  const startDateRef = useRef<HTMLInputElement>(null);
  const isCustomSelected = props.currentOption === OrderHistoryDateCardSelection.Custom;

  useEffect(() => {
    if (startDateRef && !props.showDrawer) {
      setTimeout(() => {
        startDateRef.current?.focus();
      }, 100);
    }
  }, [startDateRef]);

  const handleRadioKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
    if (!radioRefs?.length) return;
    const key = event.key.toLowerCase();

    const dateMenuItems = getDateMenuItems();

    const downArrowOrTab = (key === 'tab' && !event.shiftKey) || key === 'arrowdown';
    const upArrowOrTab = (key === 'tab' && event.shiftKey) || key === 'arrowup';

    if (props.isOpen) {
      if (
        (downArrowOrTab && index === dateMenuItems.length - 1 && !isCustomSelected) ||
        (upArrowOrTab && index === 0)
      ) {
        (props.anchorRef?.firstElementChild as HTMLButtonElement).focus();
        props.onClose();
      } else if (downArrowOrTab && index === dateMenuItems.length - 1 && isCustomSelected) {
        startDateRef.current?.focus();
      }
      if (index === dateMenuItems.length - 1 && ['tab', 'arrowup'].includes(key)) {
        if (event.shiftKey || key === 'arrowup') {
          radioRefs?.[index - 1]?.current?.focus();
        }
        event.preventDefault();
      }
      if (index < dateMenuItems.length - 1) {
        if (downArrowOrTab) {
          radioRefs?.[index + 1]?.current?.focus();
        } else if (upArrowOrTab) {
          if (index !== 0) {
            radioRefs?.[index - 1]?.current?.focus();
          }
        }
        event.preventDefault();
      }
      if (key === 'enter') {
        onDateOptionChange(dateMenuItems[index].value as string);
        if (index !== dateMenuItems.length - 1) {
          (props.anchorRef?.firstElementChild as HTMLButtonElement).focus();
        }
        event.preventDefault();
      }
    }
  };

  const getDateMenuItems = (drawerItems?: boolean): RadioItem[] => {
    const items: RadioItem[] = [];
    DateMap.map((item) => {
      items.push({
        label: <Typography variant={drawerItems ? 'responsiveParagraph' : 'paragraph'}>{item.label}</Typography>,
        value: item.value,
        sx: { mb: 1 },
      });
    });

    return items;
  };
  const getDateRadioItems = (): JSX.Element[] => {
    const items: JSX.Element[] = [];
    getDateMenuItems(true).map((item: RadioItem, index) => {
      items.push(
        <Box
          key={index}
          sx={{
            height: (theme) => ({ xs: theme.spacing(4), xl: theme.spacing(3.25) }),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CfRadio
            item={item}
            variant='primary'
            isAutoFocusChecked
            size='small'
            inputRef={radioRefs[index]}
            onMouseDown={(item: RadioItem) => {
              onDateOptionChange(item.value as string);
            }}
            onKeyDown={(event) => handleRadioKeyDown(event, index)}
            isChecked={item.value === props.currentOption}
            sx={{ pl: 2, typography: 'responsiveParagraph' }}
          />
        </Box>
      );
    });

    return items;
  };

  const onDateOptionChange = (value: string): void => {
    let _endDate = truncateTime(new Date());
    let _startDate = truncateTime(new Date());
    _endDate = addDays(_endDate, 1);

    switch (value) {
      case OrderHistoryDateCardSelection.UpcomingAndRecent:
        _startDate = truncateTime(addDays(_startDate, -30));
        _endDate = truncateTime(addDays(_endDate, 30));
        break;
      case OrderHistoryDateCardSelection.Last7Days:
        _startDate = truncateTime(addDays(_startDate, -7));
        break;
      case OrderHistoryDateCardSelection.Last30Days:
        _startDate = truncateTime(addDays(_startDate, -30));
        break;
      case OrderHistoryDateCardSelection.Last90Days:
        _startDate = truncateTime(addDays(_startDate, -90));
        break;
      case OrderHistoryDateCardSelection.Last12Months:
        _startDate = truncateTime(subtractMonths(_startDate, 12));
        break;
      case OrderHistoryDateCardSelection.YearToDate:
        _startDate = truncateTime(new Date(_endDate.getFullYear(), 0, 1));
        break;
      case OrderHistoryDateCardSelection.Custom:
        _startDate = truncateTime(new Date(history.location.state?.startDate ?? props.startDate));
        _endDate = truncateTime(new Date(history.location.state?.endDate ?? props.endDate));
        _endDate = addDays(_endDate, 1);
        break;
      default:
        break;
    }

    props.onChange(_startDate, _endDate, value as OrderHistoryDateCardSelection);
  };

  const handleDateRangeChange = (sd: Date, ed: Date): void => {
    props.onChange(sd, ed, OrderHistoryDateCardSelection.Custom);
  };

  const handleCalendarClose = () => {
    (props.anchorRef?.firstElementChild as HTMLButtonElement).focus();
    props.onClose();
  };

  const menuWidth = isCustomSelected ? { maxWidth: 243 } : { maxWidth: 182 };

  return (
    <>
      {props.isOpen && (
        <>
          {!props.showDrawer ? (
            <CfUncontrolledMenu
              anchorElement={props.isOpen && props.anchorRef}
              mode='menu'
              variant='round'
              closeOnScroll
              onClose={props.onClose}
              keepMounted
              disablePortal
              keepOpenOnChange
              placement='bottom'
              width={243}
              containerSx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: 0,
              }}
              popperSx={{
                ...menuWidth,
                border: (theme) => '1px solid ' + theme.palette.coolGrey[200],
                borderRadius: 2,
                boxShadow: '0px 2px 8px 0px #00000026',
                overflow: 'visible',
                maxHeight: '260px',
              }}
            >
              <RadioGroup>{getDateRadioItems()}</RadioGroup>
              {isCustomSelected && (
                <CfMultipleDatePicker
                  startDateRef={startDateRef}
                  startDate={new Date(history.location.state?.startDate ?? props.startDate)}
                  endDate={new Date(history.location.state?.endDate ?? props.endDate)}
                  onChange={handleDateRangeChange}
                  onEndDateCalendarClose={handleCalendarClose}
                  errorSx={{ pl: 2, pr: 2, pb: 1 }}
                  sx={{
                    pl: 5,
                    pb: 1,
                    '& .MuiInputBase-root .MuiInput-input': {
                      px: 1,
                    },
                    '& .MuiInputBase-root.Mui-focused:not(.Mui-error) .MuiInput-input': {
                      px: (theme) => theme.custom.spacing - 1 + 'px',
                    },
                  }}
                  inputSx={{ width: '80px' }}
                />
              )}
            </CfUncontrolledMenu>
          ) : (
            <CfUncontrolledDrawer
              mode='menu'
              open={props.isOpen}
              keepMounted
              disablePortal
              onClose={props.onClose}
              hideDividers
              keepOpenOnChange
              placement='bottom'
              drawerSx={{ top: 'unset !important' }}
              prefix={
                <CfDrawerHeader
                  onClose={props.onClose}
                  sx={{ color: (theme: Theme) => theme.palette.common.black, pt: 1 }}
                  closeButtonSx={{ color: (theme: Theme) => theme.palette.coolGrey[400] }}
                >
                  Date selector
                </CfDrawerHeader>
              }
            >
              <RadioGroup sx={{ gap: 0.5, mt: 1 }}>{getDateRadioItems()}</RadioGroup>
              {props.currentOption === OrderHistoryDateCardSelection.Custom && (
                <CfMultipleDatePicker
                  startDateRef={startDateRef}
                  startDate={new Date(history.location.state.startDate ?? props.startDate)}
                  startDatePickerPlacement='top'
                  endDate={new Date(history.location.state?.endDate ?? props.endDate)}
                  endDatePickerPlacement='top'
                  onChange={handleDateRangeChange}
                  onEndDateCalendarClose={handleCalendarClose}
                  errorSx={{ pl: 2, pr: 2, pb: 1 }}
                  sx={{
                    pl: 5,
                    pb: 2,
                    pt: 2,
                    '& .MuiInputBase-root .MuiInput-input': {
                      pl: 1.75,
                      pr: 1.75,
                    },
                    '& .MuiInputBase-root.Mui-focused .MuiInput-input': {
                      px: (theme) => theme.custom.spacing - 1 + 'px',
                    },
                  }}
                />
              )}
            </CfUncontrolledDrawer>
          )}
        </>
      )}
    </>
  );
};

export default ProductListDiaryDateMenu;
